import React, { useEffect, useRef } from "react";
import PropTypes from "prop-types";

import gsap from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";

import { useStore } from "@Store/";

import Container from "@components/Container";
import RichText from "@components/RichText";

import {
  ModelInfoMain,
  ModelInfoBackground,
  ModelInfoOverlay,
  ModelInfoWrapper,
  ModelInfoLabel,
  ModelInfoBody,
  ModelInfoTitle,
  ModelInfoContent,
} from "./index.style";

gsap.registerPlugin(ScrollTrigger);

const getAssetUrl = (asset) => {
  const url = asset?.file?.url;

  if (!url) return null;

  return url.startsWith("//") ? `https:${url}` : url;
};

const OurModelInfo = ({
  title,
  content,
  background,
  id,
  sectionTitle,
}) => {
  const [store] = useStore();

  const sectionRef = useRef(null);
  const labelRef = useRef(null);
  const titleRef = useRef(null);
  const contentRef = useRef(null);
  const smoothScrollRef = useRef(store?.smoothScroll);

  const sectionId = id || "our-model-info";
  const backgroundUrl = getAssetUrl(background);

  smoothScrollRef.current = store?.smoothScroll;

  useEffect(() => {
    if (typeof window === "undefined") {
      return undefined;
    }

    const section = sectionRef.current;
    const labelElement = labelRef.current;
    const titleElement = titleRef.current;
    const contentElement = contentRef.current;

    if (!section) {
      return undefined;
    }

    let firstFrameId;
    let secondFrameId;

    const refresh = () => {
      if (
        smoothScrollRef.current &&
        typeof smoothScrollRef.current.refresh === "function"
      ) {
        smoothScrollRef.current.refresh();
      } else {
        ScrollTrigger.refresh();
      }
    };

    const refreshAfterLayout = () => {
      if (firstFrameId) {
        cancelAnimationFrame(firstFrameId);
      }

      if (secondFrameId) {
        cancelAnimationFrame(secondFrameId);
      }

      firstFrameId = requestAnimationFrame(() => {
        secondFrameId = requestAnimationFrame(() => {
          refresh();
        });
      });
    };

    const handleResize = () => {
      gsap.matchMediaRefresh();
      refreshAfterLayout();
    };

    const ctx = gsap.context(() => {
      const mm = gsap.matchMedia();

      mm.add("(min-width:1024px)", () => {
        if (contentElement) {
          gsap.set(contentElement, {
            "--line-progress": 0,
          });
        }

        const timeline = gsap.timeline({
          defaults: {
            duration: 0.9,
            ease: "power3.out",
          },
          scrollTrigger: {
            trigger: section,
            start: "top 75%",
            toggleActions: "play none none none",
            once: true,
            invalidateOnRefresh: true,
          },
        });

        if (labelElement) {
          timeline.fromTo(
            labelElement,
            {
              x: -80,
              autoAlpha: 0,
            },
            {
              x: 0,
              autoAlpha: 1,
            },
          );
        }

        if (titleElement) {
          timeline.fromTo(
            titleElement,
            {
              y: 80,
              autoAlpha: 0,
            },
            {
              y: 0,
              autoAlpha: 1,
            },
            labelElement ? "-=0.55" : 0,
          );
        }

        if (contentElement) {
          timeline.to(
            contentElement,
            {
              "--line-progress": 1,
              duration: 1.4,
              ease: "power2.inOut",
            },
            titleElement ? "-=0.55" : 0,
          );

          timeline.fromTo(
            contentElement,
            {
              y: 60,
              autoAlpha: 0,
            },
            {
              y: 0,
              autoAlpha: 1,
            },
            "<",
          );
        }

        refreshAfterLayout();

        return () => {
          timeline.scrollTrigger?.kill();
          timeline.kill();
        };
      });

      mm.add("(max-width:1023px)", () => {
        const elements = [
          labelElement,
          titleElement,
          contentElement,
        ].filter(Boolean);

        gsap.set(elements, {
          clearProps: "all",
        });

        if (contentElement) {
          gsap.set(contentElement, {
            "--line-progress": 1,
          });
        }
      });

      refreshAfterLayout();

      return () => {
        mm.revert();
      };
    }, section);

    window.addEventListener(
      "load",
      refreshAfterLayout,
    );

    window.addEventListener(
      "resize",
      handleResize,
    );

    return () => {
      if (firstFrameId) {
        cancelAnimationFrame(firstFrameId);
      }

      if (secondFrameId) {
        cancelAnimationFrame(secondFrameId);
      }

      ctx.revert();

      window.removeEventListener(
        "load",
        refreshAfterLayout,
      );

      window.removeEventListener(
        "resize",
        handleResize,
      );
    };
  }, [store?.smoothScroll]);

  return (
    <ModelInfoMain
      id={sectionId}
      ref={sectionRef}
    >
      <ModelInfoBackground
        $backgroundUrl={backgroundUrl}
        aria-hidden="true"
      />

      <Container>
        <ModelInfoWrapper>
          <ModelInfoLabel ref={labelRef}>
            {sectionTitle}
          </ModelInfoLabel>

          <ModelInfoBody>
            {title && (
              <ModelInfoTitle ref={titleRef}>
                <RichText content={title} />
              </ModelInfoTitle>
            )}

            {content && (
              <ModelInfoContent ref={contentRef}>
                <RichText content={content} />
              </ModelInfoContent>
            )}
          </ModelInfoBody>
        </ModelInfoWrapper>
      </Container>
    </ModelInfoMain>
  );
};

OurModelInfo.propTypes = {
  id: PropTypes.string,
  sectionTitle: PropTypes.string,
  title: PropTypes.object,
  content: PropTypes.object,
  background: PropTypes.shape({
    file: PropTypes.shape({
      url: PropTypes.string,
      contentType: PropTypes.string,
    }),
  }),
};

OurModelInfo.defaultProps = {
  id: "our-model-info",
  sectionTitle: "",
  title: null,
  content: null,
  background: null,
};

export default OurModelInfo;